import cardValidator from 'card-validator';
import { raygun } from '@ynap/clientside-monitoring';
import type { CreditCardMessages, CreditCardDetails, InputState, FormMeta, SynchronousErrors, CardDetails, ExpiryDate } from './@types';

export const DEFAULT_CARD_NUMBER_MASK = '9999 9999 9999 9999';
export const AMEX_CARD_NUMBER_MASK = '9999 999999 99999';
export const UNION_PAY_CARD_NUMBER_MASK = '999999 9999999999999';
export const MAESTRO_CARD_NUMBER_MASK = '9999 9999 9999 9999 999';

export const buildLabels = (
    messages: { creditCardForm: CreditCardMessages },
    creditCardDetails: CreditCardDetails,
): {
    number: string;
    cardHolderName: string;
    expiryDate: string;
    month: string;
    monthPlaceholder: string;
    year: string;
    yearPlaceholder: string;
    cvv: string;
    cvvHelp: string;
    cardDetails?: string;
    cardNumberFormat?: string;
    expiryDateFormat?: string;
    cvvFormat?: string;
} => ({
    number: messages.creditCardForm.number(creditCardDetails),
    cardHolderName: messages.creditCardForm.cardHolderName(creditCardDetails),
    expiryDate: messages.creditCardForm.expiryDate(creditCardDetails),
    month: messages.creditCardForm.month(creditCardDetails),
    monthPlaceholder: messages.creditCardForm.monthPlaceholder(creditCardDetails),
    year: messages.creditCardForm.year(creditCardDetails),
    yearPlaceholder: messages.creditCardForm.yearPlaceholder(creditCardDetails),
    cvv: messages.creditCardForm.cvv(creditCardDetails),
    cvvHelp: messages.creditCardForm.cvvHelp(creditCardDetails),
    cardDetails: messages.creditCardForm.cardDetails?.(creditCardDetails),
    cardNumberFormat: messages.creditCardForm.cardNumberFormat?.(creditCardDetails),
    expiryDateFormat: messages.creditCardForm.expiryDateFormat?.(),
    cvvFormat: messages.creditCardForm.cvvFormat?.(creditCardDetails),
});

export const handleExpiryDateMask = (newState: InputState): InputState => {
    let { value } = newState;
    const { selection } = newState;

    if (value && value.length === 7) {
        const [mm, year] = value.split('/');
        value = `${mm}/${year.substr(2)}`;
    }

    return { value, selection };
};

export const getCreditCardMask = (value: string | undefined): string => {
    const ruleSet = [
        {
            // Visa Electron
            mask: DEFAULT_CARD_NUMBER_MASK,
            type: 'visa',
        },
        {
            // American Express
            mask: AMEX_CARD_NUMBER_MASK,
            type: 'american-express',
        },
        {
            // Maestro 16+
            mask: MAESTRO_CARD_NUMBER_MASK,
            type: 'maestro',
        },
        {
            // Mastercard
            mask: DEFAULT_CARD_NUMBER_MASK,
            type: 'mastercard',
        },
        {
            // China UnionPay (18)
            mask: UNION_PAY_CARD_NUMBER_MASK,
            type: 'unionpay',
        },
    ];

    if (!value) {
        return DEFAULT_CARD_NUMBER_MASK;
    }

    const rule = cardValidator.number(value);
    return ruleSet.find((el) => el.type === rule?.card?.type)?.mask || DEFAULT_CARD_NUMBER_MASK;
};

export const getFormErrors = (formMeta: FormMeta, synchronousErrors: SynchronousErrors, isCardWallet?: boolean): string | null => {
    if (isCardWallet) {
        if (
            !formMeta ||
            !synchronousErrors ||
            !Object.keys(synchronousErrors).length
        ) {
            return null;
        }

        let firstError: string | null = null;

        // eslint-disable-next-line no-restricted-syntax
        for (const key in synchronousErrors) {
            const currentError = synchronousErrors[key];

            if (currentError && formMeta[key]?.touched) {
                firstError = currentError;
                break;
            }
        }

        return firstError;
    }

    if (
        !formMeta?.creditCard ||
        !synchronousErrors.creditCard ||
        !Object.keys(synchronousErrors.creditCard).length
    ) {
        return null;
    }

    let firstError: string | null = null;

    // eslint-disable-next-line no-restricted-syntax
    for (const key in synchronousErrors.creditCard) {
        const currentError = synchronousErrors.creditCard[key];

        if (currentError && formMeta.creditCard[key]?.touched) {
            firstError = currentError;
            break;
        }
    }

    return firstError;
};

export const getCardTypeFromValidator = (numberValue: string): CardDetails => {
    const validatorType = cardValidator.number(numberValue);
    const { card } = validatorType;

    if (numberValue?.length > 4 && card === null) {
        // Do not trigger tracking until the card number is long enough.
        raygun.helpers.agent('send', {
            error: new Error('Unknown card type'),
            tags: ['checkout', 'unknown-card-type', 'credit-card', 'card-validation'],
        });
    }

    return card
        ? {
              cardType: card.type,
              isAmex: card.type === 'american-express',
              cvvType: card.code.name,
              cvvSize: card.code.size,
          }
        : { cvvType: 'CVV', cvvSize: 0 };
};

export const normalize = (value: string, previousValue: string | undefined, maxLength: number, allowPasting: boolean): string => {
    if (!value) return value;

    if (allowPasting && !previousValue) {
        // Allows autofill to work correctly for year field when saved in YYYY format.
        return value.slice(-maxLength);
    }

    return value.slice(0, maxLength);
};

export const normalizeExpiryDate = (current: ExpiryDate, prevValue?: ExpiryDate): ExpiryDate => {
    const { month: prevMonth, year: prevYear } = prevValue || {};
    return {
        month: normalize(current.month, prevMonth, 2, false),
        year: normalize(current.year, prevYear, 2, true),
    };
};
